import { DatePipe, DecimalPipe } from '@angular/common'
import { HttpParams } from '@angular/common/http'
import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { switchMap } from 'rxjs/operators'

import {
    BaseComponent,
    CREW_SCHEDULE_CONTEXT_DAYS,
    CREW_SCHEDULE_WEEKDAYS,
    CrewMemberReassignment,
    CrewScheduleContext,
    CrewScheduleDayAssignment,
    FullCrewScheduleProjection,
    FullScheduleSegmentInput,
    IsoDate,
    SavedForemanSegment,
    SavedWeekendEntry,
    VpJob,
    addDaysIso,
    collectWeekdaysFrom,
    isoDateStringForApi,
    todayIso,
} from '../../../shared'

export interface DashboardKpis {
    foremanCount: number
    crewMemberCount: number
    crewsWorkingToday: number
    peopleWorkingToday: number
    peopleOffToday: number
    activeJobCount: number
    crewsRunningOutCount: number
    coveragePercent: number
    jobsOverEstimateCount: number
    activeLendCount: number
}

export interface RunningOutRowVm {
    hrRef: number
    name: string
    scheduledDays: number
    lastWorkLabel: string
    hasWork: boolean
}

export interface JobOverEstimateRowVm {
    vpJobId: string
    jobName: string
    phase: string
    estHours: number
    projHours: number
    overHours: number
    crewCount: number
}

export interface UpcomingPtoRowVm {
    key: string
    memberName: string
    foremanName: string
    startDate: IsoDate
    endDate: IsoDate
    leaveType: string
    status: 'Approved' | 'Pending'
}

export interface CrewCoverageRowVm {
    hrRef: number
    name: string
    scheduledDays: number
    totalHours: number
    coveragePercent: number
}

function newClientId(): string {
    return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `seg-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

@Component({
    selector: 'app-foreman-schedule-dashboard-tab',
    templateUrl: './foreman-schedule-dashboard-tab.component.html',
    styleUrls: ['./foreman-schedule-dashboard-tab.component.css'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DatePipe, DecimalPipe],
})
export class ForemanScheduleDashboardTabComponent extends BaseComponent {
    private readonly destroyRef = inject(DestroyRef)

    readonly isActive = input(false)
    readonly activeJobs = input.required<VpJob[]>()
    readonly reassignments = input<CrewMemberReassignment[]>([])
    readonly savedSegments = input<SavedForemanSegment[]>([])
    readonly savedWeekendEntries = input<SavedWeekendEntry[]>([])
    /** Bumped by the shell when the foreman roster changes; triggers a context reload. */
    readonly foremanRevision = input(0)

    readonly isLoading = signal(false)
    readonly loadError = signal<string | null>(null)
    readonly context = signal<CrewScheduleContext | null>(null)
    readonly projection = signal<FullCrewScheduleProjection | null>(null)

    protected readonly horizonWeekdays = CREW_SCHEDULE_WEEKDAYS

    private hasLoaded = false
    private reassignmentsSnapshot = ''
    private savedSegmentsSnapshot = ''
    private savedWeekendEntriesSnapshot = ''
    private foremanRevisionSnapshot = 0

    readonly gridWeekdays = computed(() => collectWeekdaysFrom(todayIso(), CREW_SCHEDULE_WEEKDAYS))

    readonly gridStart = computed(() => {
        const weekdays = this.gridWeekdays()
        return weekdays.length ? weekdays[0] : todayIso()
    })

    readonly gridRangeEnd = computed(() => {
        const weekdays = this.gridWeekdays()
        return weekdays.length ? weekdays[weekdays.length - 1] : todayIso()
    })

    /** Per-foreman map of date -> primary scheduled day assignment (hours > 0) within the window. */
    private readonly assignmentsByForeman = computed(() => {
        const map = new Map<number, Map<IsoDate, CrewScheduleDayAssignment>>()
        const proj = this.projection()
        if (!proj) return map
        const windowDates = new Set(this.gridWeekdays())
        for (const f of proj.foremen ?? []) {
            const dayMap = new Map<IsoDate, CrewScheduleDayAssignment>()
            for (const s of f.segments ?? []) {
                for (const d of s.days ?? []) {
                    if (d.hoursScheduled > 0) {
                        const date = isoDateStringForApi(d.date as string | Date | null | undefined)
                        if (date && windowDates.has(date)) dayMap.set(date, { ...d, date })
                    }
                }
            }
            map.set(f.foremanHrRef, dayMap)
        }
        return map
    })

    readonly kpis = computed((): DashboardKpis => {
        const ctx = this.context()
        const weekdays = this.gridWeekdays()
        const assignments = this.assignmentsByForeman()
        const today = todayIso()

        if (!ctx) {
            return {
                foremanCount: 0,
                crewMemberCount: 0,
                crewsWorkingToday: 0,
                peopleWorkingToday: 0,
                peopleOffToday: 0,
                activeJobCount: 0,
                crewsRunningOutCount: 0,
                coveragePercent: 0,
                jobsOverEstimateCount: 0,
                activeLendCount: 0,
            }
        }

        const foremanCount = ctx.foremen.length
        const crewMemberCount = this.distinctMemberHrRefs().size

        let crewsWorkingToday = 0
        let peopleWorkingToday = 0
        for (const f of ctx.foremen) {
            const dayMap = assignments.get(f.hrRef)
            const todayAssignment = dayMap?.get(today)
            if (todayAssignment) {
                crewsWorkingToday++
                peopleWorkingToday += todayAssignment.availableCrewCount
            }
        }

        const peopleOffToday = this.membersOffOn(today).size

        const activeJobs = new Set<string>()
        let scheduledCrewDays = 0
        let crewsRunningOutCount = 0
        const lastWeekday = weekdays.length ? weekdays[weekdays.length - 1] : today
        for (const f of ctx.foremen) {
            const dayMap = assignments.get(f.hrRef) ?? new Map<IsoDate, CrewScheduleDayAssignment>()
            let scheduledDays = 0
            let lastWork: IsoDate | null = null
            for (const date of weekdays) {
                const a = dayMap.get(date)
                if (a) {
                    scheduledDays++
                    activeJobs.add((a.vpJobId ?? '').trim())
                    lastWork = date
                }
            }
            scheduledCrewDays += scheduledDays
            if (!lastWork || lastWork < lastWeekday) crewsRunningOutCount++
        }
        activeJobs.delete('')

        const totalCrewDays = foremanCount * weekdays.length
        const coveragePercent = totalCrewDays > 0 ? Math.round((scheduledCrewDays / totalCrewDays) * 100) : 0

        const jobsOverEstimateCount = (this.projection()?.pools ?? []).filter(p => (p.overThresholdHours ?? 0) > 0).length

        const activeLendCount = this.activeLendCount()

        return {
            foremanCount,
            crewMemberCount,
            crewsWorkingToday,
            peopleWorkingToday,
            peopleOffToday,
            activeJobCount: activeJobs.size,
            crewsRunningOutCount,
            coveragePercent,
            jobsOverEstimateCount,
            activeLendCount,
        }
    })

    readonly runningOutRows = computed((): RunningOutRowVm[] => {
        const ctx = this.context()
        if (!ctx) return []
        const weekdays = this.gridWeekdays()
        const assignments = this.assignmentsByForeman()
        const lastWeekday = weekdays.length ? weekdays[weekdays.length - 1] : todayIso()

        const rows: RunningOutRowVm[] = []
        for (const f of ctx.foremen) {
            const dayMap = assignments.get(f.hrRef) ?? new Map<IsoDate, CrewScheduleDayAssignment>()
            let scheduledDays = 0
            let lastWork: IsoDate | null = null
            for (const date of weekdays) {
                if (dayMap.get(date)) {
                    scheduledDays++
                    lastWork = date
                }
            }
            const hasWork = scheduledDays > 0
            // A crew "running out" has no work at all, or finishes before the horizon ends.
            if (hasWork && (lastWork ?? '') >= lastWeekday) continue
            rows.push({
                hrRef: f.hrRef,
                name: f.name,
                scheduledDays,
                lastWorkLabel: lastWork ? this.formatShortDate(lastWork) : 'None',
                hasWork,
            })
        }

        return rows.sort((a, b) => a.scheduledDays - b.scheduledDays || a.name.localeCompare(b.name))
    })

    readonly jobsOverEstimateRows = computed((): JobOverEstimateRowVm[] => {
        const pools = this.projection()?.pools ?? []
        return pools
            .filter(p => (p.overThresholdHours ?? 0) > 0)
            .map(p => ({
                vpJobId: (p.vpJobId ?? '').trim(),
                jobName: this.jobName(p.vpJobId),
                phase: (p.phase ?? '').trim(),
                estHours: p.estHours ?? 0,
                projHours: p.projHours ?? 0,
                overHours: p.overThresholdHours ?? 0,
                crewCount: p.crewCount ?? 0,
            }))
            .sort((a, b) => b.overHours - a.overHours)
    })

    readonly upcomingPtoRows = computed((): UpcomingPtoRowVm[] => {
        const ctx = this.context()
        if (!ctx) return []
        const start = this.gridStart()
        const end = this.gridRangeEnd()
        const rows: UpcomingPtoRowVm[] = []
        const seen = new Set<string>()

        for (const f of ctx.foremen) {
            for (const m of f.members ?? []) {
                for (const t of m.timeOff ?? []) {
                    const s = isoDateStringForApi(t.startDate as string | Date | null | undefined)
                    const e = isoDateStringForApi(t.endDate as string | Date | null | undefined)
                    if (!s || !e) continue
                    // Overlap with the window.
                    if (e < start || s > end) continue
                    const key = `${m.hrRef}|${t.leaveRequestId}|${s}|${e}`
                    if (seen.has(key)) continue
                    seen.add(key)
                    rows.push({
                        key,
                        memberName: m.name,
                        foremanName: f.name,
                        startDate: s,
                        endDate: e,
                        leaveType: t.leaveType?.trim() || 'Time off',
                        status: t.status,
                    })
                }
            }
        }

        return rows.sort((a, b) => a.startDate.localeCompare(b.startDate) || a.memberName.localeCompare(b.memberName))
    })

    readonly crewCoverageRows = computed((): CrewCoverageRowVm[] => {
        const ctx = this.context()
        if (!ctx) return []
        const weekdays = this.gridWeekdays()
        const assignments = this.assignmentsByForeman()
        const denom = weekdays.length || 1

        return ctx.foremen
            .map(f => {
                const dayMap = assignments.get(f.hrRef) ?? new Map<IsoDate, CrewScheduleDayAssignment>()
                let scheduledDays = 0
                let totalHours = 0
                for (const date of weekdays) {
                    const a = dayMap.get(date)
                    if (a) {
                        scheduledDays++
                        totalHours += a.hoursScheduled
                    }
                }
                return {
                    hrRef: f.hrRef,
                    name: f.name,
                    scheduledDays,
                    totalHours,
                    coveragePercent: Math.round((scheduledDays / denom) * 100),
                }
            })
            .sort((a, b) => a.name.localeCompare(b.name))
    })

    constructor() {
        super()

        effect(() => {
            if (this.isActive() && !this.hasLoaded) {
                this.loadContext()
            }
        })

        effect(() => {
            const snapshot = JSON.stringify(this.reassignments())
            if (snapshot === this.reassignmentsSnapshot) return
            this.reassignmentsSnapshot = snapshot
            if (!this.hasLoaded) return
            this.runProjection()
        })

        effect(() => {
            const snapshot = JSON.stringify(this.savedSegments())
            if (snapshot === this.savedSegmentsSnapshot) return
            this.savedSegmentsSnapshot = snapshot
            if (!this.hasLoaded) return
            this.runProjection()
        })

        effect(() => {
            const snapshot = JSON.stringify(this.savedWeekendEntries())
            if (snapshot === this.savedWeekendEntriesSnapshot) return
            this.savedWeekendEntriesSnapshot = snapshot
            if (!this.hasLoaded) return
            this.runProjection()
        })

        effect(() => {
            const revision = this.foremanRevision()
            if (revision === this.foremanRevisionSnapshot) return
            this.foremanRevisionSnapshot = revision
            if (!this.hasLoaded) return
            if (this.isActive()) {
                this.refresh()
            } else {
                this.hasLoaded = false
            }
        })
    }

    refresh() {
        this.hasLoaded = false
        this.projection.set(null)
        this.loadContext()
    }

    jobName(jobId: string | null | undefined): string {
        const id = (jobId ?? '').trim()
        if (!id) return ''
        const job = this.activeJobs().find(j => (j.id ?? '').trim() === id)
        return job ? job.name : ''
    }

    private distinctMemberHrRefs(): Set<number> {
        const set = new Set<number>()
        for (const f of this.context()?.foremen ?? []) {
            for (const m of f.members ?? []) set.add(m.hrRef)
        }
        return set
    }

    /** Distinct crew members on approved/pending time off covering the given date. */
    private membersOffOn(date: IsoDate): Set<number> {
        const set = new Set<number>()
        for (const f of this.context()?.foremen ?? []) {
            for (const m of f.members ?? []) {
                for (const t of m.timeOff ?? []) {
                    const s = isoDateStringForApi(t.startDate as string | Date | null | undefined)
                    const e = isoDateStringForApi(t.endDate as string | Date | null | undefined)
                    if (s && e && s <= date && e >= date) {
                        set.add(m.hrRef)
                        break
                    }
                }
            }
        }
        return set
    }

    /** Count of reassignments active on any weekday in the window. */
    private activeLendCount(): number {
        const start = this.gridStart()
        const end = this.gridRangeEnd()
        return (this.reassignments() ?? []).filter(r => !(r.endDate < start || r.startDate > end)).length
    }

    private loadContext() {
        this.isLoading.set(true)
        this.loadError.set(null)

        const start = todayIso()
        const end = addDaysIso(start, CREW_SCHEDULE_CONTEXT_DAYS)
        const params = new HttpParams().set('startDate', start).set('endDate', end)

        this.cancelableRequest(
            'dashboard-context',
            true,
            this.services.data
                .load(
                    true,
                    false,
                    this.services.data
                        .get<{ ready: boolean }>(
                            'api/ForemanSchedule/WarmCrewScheduleProductivityCache',
                            new HttpParams(),
                            Object,
                        )
                        .pipe(
                            switchMap(() =>
                                this.services.data.get<CrewScheduleContext>(
                                    'api/ForemanSchedule/GetCrewScheduleContext',
                                    params,
                                    Object,
                                ),
                            ),
                        ),
                )
                .subscribe({
                    next: ([data]) => {
                        this.hasLoaded = true
                        this.context.set(this.normalizeContext(data))
                        this.runProjection()
                    },
                    error: () => {
                        this.isLoading.set(false)
                        this.loadError.set('Failed to load dashboard data.')
                    },
                }),
        )
    }

    private runProjection() {
        if (!this.context()) return
        this.isLoading.set(true)

        const request = {
            startDate: this.gridStart(),
            maxProjectionDays: CREW_SCHEDULE_CONTEXT_DAYS,
            hoursPerPerson: 10,
            segments: this.buildSegmentInputs(),
            weekendEntries: this.weekendEntriesToApi(),
            reassignments: this.reassignmentsToApi(),
        }

        this.services.data
            .post<FullCrewScheduleProjection>('api/ForemanSchedule/ProjectFullCrewSchedule', request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: projection => {
                    this.projection.set(projection)
                    this.isLoading.set(false)
                },
                error: () => {
                    this.isLoading.set(false)
                    this.loadError.set('Failed to project the dashboard data.')
                },
            })
    }

    private buildSegmentInputs(): FullScheduleSegmentInput[] {
        const saved = (this.savedSegments() ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder)
        return saved.map(s => ({
            clientId: newClientId(),
            foremanHrRef: s.foremanHrRef,
            vpJobId: (s.vpJobId ?? '').trim(),
            phase: (s.phase ?? '').trim(),
            startDate: isoDateStringForApi(s.startDate as string | Date | null | undefined),
            sortOrder: s.sortOrder,
            endDateExclusive: null,
        }))
    }

    private weekendEntriesToApi(): SavedWeekendEntry[] {
        return (this.savedWeekendEntries() ?? []).map(e => ({
            id: e.id,
            foremanHrRef: e.foremanHrRef,
            date: isoDateStringForApi(e.date as string | Date | null | undefined),
            vpJobId: e.vpJobId,
            phase: e.phase,
            hours: e.hours ?? 0,
        }))
    }

    private reassignmentsToApi() {
        return (this.reassignments() ?? []).map(r => ({
            memberHrRef: r.memberHrRef,
            fromForemanHrRef: r.fromForemanHrRef,
            toForemanHrRef: r.toForemanHrRef,
            startDate: r.startDate,
            endDate: r.endDate,
        }))
    }

    private normalizeContext(data: CrewScheduleContext): CrewScheduleContext {
        return {
            ...data,
            dateRangeStart: isoDateStringForApi(data.dateRangeStart as string | Date | null | undefined),
            dateRangeEnd: isoDateStringForApi(data.dateRangeEnd as string | Date | null | undefined),
            foremen: (data.foremen ?? []).map(f => ({
                ...f,
                members: (f.members ?? []).map(m => ({
                    ...m,
                    timeOff: (m.timeOff ?? []).map(t => ({
                        ...t,
                        startDate: isoDateStringForApi(t.startDate as string | Date | null | undefined),
                        endDate: isoDateStringForApi(t.endDate as string | Date | null | undefined),
                    })),
                })),
            })),
            holidays: (data.holidays ?? []).map(h => ({
                ...h,
                date: isoDateStringForApi(h.date as string | Date | null | undefined),
            })),
        }
    }

    private formatShortDate(iso: IsoDate): string {
        const [y, m, d] = iso.split('-').map(n => parseInt(n, 10))
        const dt = new Date(Date.UTC(y, m - 1, d))
        return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
    }
}
